import {
  BIRDModelController,
  DataStateChangeEvent,
  NavigationEvent,
  TooltipDrillController,
  DataVisualElementController,
} from '@sas-dvr/ltjs-bird';
import { ofActionCreatorType } from '../util/rxUtils';
import { StateObservable } from 'redux-observable';
import { merge, Observable, of, EMPTY, from } from 'rxjs';
import {
  concatMap,
  delay,
  distinctUntilChanged,
  first,
  ignoreElements,
  map,
  mergeMap,
  publish,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { fromPortableEvent, withRetained, withRetainedArray, notNull, filterNulls } from '../util';
import { DataValue } from '@sas-dvr/ltjs-commons';
import {
  BreadcrumbState,
  BreadcrumbTarget,
  BreadcrumbItemTarget,
  BasicHierarchyData,
  HierarchyData,
} from '@sas-dvr/internal-va-react-core/store/breadcrumb/BreadcrumbState';
import { AnyAction } from 'redux';
import { LocaleKey } from '@sas-dvr/internal-va-react-core/locale/LocaleKey';

export const loadBreadcrumb = (
  dataVisualElementController: DataVisualElementController,
  _unload$: Observable<BIRDModelController>,
  myActions$: Observable<AnyAction>,
  state$: StateObservable<ReportState>,
  key: string
): Observable<AnyAction> => {
  const basicDataActions$ = fromPortableEvent(
    dataVisualElementController,
    DataStateChangeEvent.DATACHANGE
  ).pipe(
    map(() => getBasicData(dataVisualElementController)),
    map((data) => BreadcrumbState.actions.setBasicData(key, data))
  );

  const _openActionData$ = myActions$.pipe(
    ofActionCreatorType<typeof BreadcrumbState.actions.openBreadcrumb>(
      BreadcrumbState.actionTypes.openBreadcrumb
    ),
    map((action) => action.payload.data)
  );

  const _targetsToUpdate$ = state$.pipe(
    map((aState) => aState.breadcrumbs[key] || []),
    distinctUntilChanged(),
    publish((myState$) =>
      merge(
        // For each openBreadcrumb action, fetch new data if necessary
        _openActionData$.pipe(
          withLatestFrom(myState$),
          map(([target, myState]) => {
            const hierarchy = myState.find(({ name }) => name === target.name);
            const crumbData = hierarchy && hierarchy.items[target.index];
            return crumbData && crumbData.status !== 'complete' ? [target] : [];
          })
        ),
        // For each change in state, fetch data for the newly added crumbs
        myState$.pipe(
          map((myState) => {
            const updates: BreadcrumbTarget[] = [];
            myState.forEach(({ name, items }) => {
              items.forEach((item, index) => {
                if (item.status === 'initial') {
                  updates.push({ name, index });
                }
              });
            });
            return updates;
          }),
          // Run these requests on a delay... the visual gets stuck in a busy state otherwise
          delay(0)
        )
      )
    )
  );

  const updateActions$ = _targetsToUpdate$.pipe(
    // Switch to cancel previous requests
    switchMap((requests: BreadcrumbTarget[]) =>
      from(requests).pipe(
        // Concat to prevent requests from running concurrently... portable won't resolve all requests otherwise
        concatMap((target) =>
          fetchPortableDataValues(dataVisualElementController, target).pipe(
            map((values) => (values || []).map((v) => (v && v.getDisplayString()) || '')),
            map((labels) =>
              BreadcrumbState.actions.updateBreadcrumb(key, {
                target,
                labels,
              })
            )
          )
        )
      )
    )
  );

  const selectHandler$ = myActions$.pipe(
    ofActionCreatorType<typeof BreadcrumbState.actions.selectBreadcrumb>(
      BreadcrumbState.actionTypes.selectBreadcrumb
    ),
    tap((action) => drillToLevel(dataVisualElementController, action.payload.data)),
    ignoreElements()
  );

  const selectItemHandler$ = myActions$.pipe(
    ofActionCreatorType<typeof BreadcrumbState.actions.selectBreadcrumbItem>(
      BreadcrumbState.actionTypes.selectBreadcrumbItem
    ),
    mergeMap((action) =>
      drillToItem(dataVisualElementController, action.payload.data, state$.value.breadcrumbs[key])
    ),
    ignoreElements()
  );

  return merge(basicDataActions$, updateActions$, selectHandler$, selectItemHandler$);
};

function getBasicData(
  dataVisualElementController: DataVisualElementController
): BasicHierarchyData[] {
  const controllers = filterNulls(dataVisualElementController.getAllDrillControllers());
  return controllers
    .map((c): BasicHierarchyData | null => {
      const name = c.getHierarchyName();
      if (!name) return null;
      return {
        name,
        label: getDrillLabel(c),
        items: getCurrentBreadcrumbLabels(c),
      };
    })
    .filter((value): value is BasicHierarchyData => value !== null);
}
function getDrillLabel(controller: TooltipDrillController): string | LocaleKey {
  const label = controller.getLabel();
  return label ? { key: 'Breadcrumb.all.fmt', args: { label } } : '';
}
function getCurrentBreadcrumbLabels(controller: TooltipDrillController): string[] {
  const values = controller.getConstantLevelValues();
  return (values || []).map((v) => v || '');
}

function drillToLevel(
  dataVisualElementController: DataVisualElementController,
  target: BreadcrumbTarget
): void {
  const controller = drillControllerByName(dataVisualElementController, target.name);
  if (controller) controller.drillUp(target.index + 1);
}

function drillToItem(
  dataVisualElementController: DataVisualElementController,
  target: BreadcrumbItemTarget,
  state: HierarchyData[] | undefined
): Observable<never> {
  const drillController = drillControllerByName(dataVisualElementController, target.name);
  if (!drillController || !state) return EMPTY;

  const drillState = state.find((s) => s.name === target.name);
  if (!drillState) return EMPTY;

  const currentItems = drillState.items[target.index];
  if (currentItems === undefined) return EMPTY;

  const itemText = currentItems.values[target.valueIndex];

  return fetchPortableDataValues(dataVisualElementController, target).pipe(
    withRetained(drillController),
    tap((values) => {
      // Search for portable value by display string
      const value = values.find((v) => v !== null && v.getDisplayString() === itemText);
      if (value) {
        drillController.drillDownFromLevel(target.index, value);
      }
    }),
    ignoreElements()
  );
}

function fetchPortableDataValues(
  dataVisualElementController: DataVisualElementController,
  target: BreadcrumbTarget
): Observable<(DataValue | null)[]> {
  const drillController = drillControllerByName(dataVisualElementController, target.name);
  const resultController = dataVisualElementController.getResultControllers()?.[0];
  if (!drillController || !resultController) return EMPTY;

  const dataSet = resultController.getDataSet();
  if (!dataSet) return EMPTY;

  const dataSetName = dataSet.getName();
  if (!dataSetName) return EMPTY;

  const dataValues$ = streamNavigationData(dataVisualElementController).pipe(
    map((data) => {
      if (
        data &&
        data.navigationLevelIndex === target.index &&
        data.hierarchyName === target.name
      ) {
        return data.values;
      }
      return null;
    }),
    notNull(),
    first(),
    shareReplay(1)
  );

  // Subscribe to navigation events now and replay later, just in case portable emits the event synchronously.
  dataValues$.subscribe();

  // Portable api to get the drillable values at this index.
  // This will trigger a NAVIGATION_DATA_UPDATE event with the list of drill values.
  dataVisualElementController.retrieveUniqueValuesForLevel(target.name, dataSetName, target.index);
  return dataValues$;
}

interface NavigationData {
  hierarchyName: string;
  navigationLevelIndex: number;
  values: (DataValue | null)[];
}
function streamNavigationData(
  dataVisualElementController: DataVisualElementController
): Observable<NavigationData> {
  return fromPortableEvent<NavigationEvent>(
    dataVisualElementController,
    NavigationEvent.NAVIGATION_DATA_UPDATE
  ).pipe(
    mergeMap((event) => {
      const values = event.getValueList();
      const hierarchyName = event.getNavigationHierarchyName();
      const navigationLevelIndex = event.getNavigationLevelIndex();
      if (!values || !hierarchyName) {
        return EMPTY;
      }
      return of({ hierarchyName, navigationLevelIndex, values }).pipe(withRetainedArray(values));
    })
  );
}

function drillControllerByName(
  dataVisualElementController: DataVisualElementController,
  name: string
): TooltipDrillController | null {
  const drillControllers = filterNulls(dataVisualElementController.getAllDrillControllers());
  return drillControllers.find((dc) => dc.getHierarchyName() === name) || null;
}
